import { Container, TextInput } from '@loomhq/lens';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { KEY_ENTER, KEY_ESCAPE } from 'constants/keyCodes';
import {
  updateUsername,
  videoPlayerUpdateAnonymousReactionMode,
} from 'creators/videoPlayer';

import useAnonymousUsername from 'hooks/useAnonymousUsername';

import {
  SharedAuthButton,
  SharedAuthSource,
} from 'components/slack/shared-auth-button';

import styles from './styles.module.less';

const DEBOUNCE_TYPING_TIMEOUT_MS = 1000;

const isInvalidName = name => {
  return /[@!\$|<>]+/.test(name);
};

const AnonymousNameInput = () => {
  const dispatch = useDispatch();
  const anonUserName = useAnonymousUsername();
  const [anonName, setAnonName] = useState(anonUserName);
  const [showInvalidUsername, setShowInvalidUsername] = useState(false);

  const nameInputRef = useRef();
  const connectWithLoomRef = useRef();

  let onChangeDebounceTimer;

  useEffect(() => {
    if (nameInputRef.current) {
      nameInputRef.current.focus({ preventScroll: true });
    }
  }, [nameInputRef]);

  const checkAndUpdateUsername = name => {
    const validName = !isInvalidName(name);

    setShowInvalidUsername(!validName);

    if (validName) {
      dispatch(videoPlayerUpdateAnonymousReactionMode(false));
    }

    if (name && validName) {
      dispatch(updateUsername(name));
    }
  };

  const onInputChange = event => {
    const name = event.target.value;

    setAnonName(name);
    setShowInvalidUsername(isInvalidName(name));

    window.clearTimeout(onChangeDebounceTimer);

    onChangeDebounceTimer = window.setTimeout(() => {
      checkAndUpdateUsername(name);
    }, DEBOUNCE_TYPING_TIMEOUT_MS);
  };

  const onKeyDown = () => {
    if (KEY_ENTER.indexOf(event.key) > -1) {
      window.clearTimeout(onChangeDebounceTimer);

      checkAndUpdateUsername(event.target.value);
    }

    if (KEY_ESCAPE.indexOf(event.key) > -1) {
      dispatch(videoPlayerUpdateAnonymousReactionMode(false));
      setShowInvalidUsername(false);
    }
  };

  return (
    <div className={classNames(styles.anonymousNameInputWrapper, 'theme-dark')}>
      <Container
        className={styles.anonymousNameInputContainer}
        width={{
          xsmall: '100%',
          small: '100%',
          medium: '80%',
          large: '80%',
        }}
      >
        <TextInput
          data-testid="anon-reaction-name-input"
          ref={nameInputRef}
          hasError={showInvalidUsername}
          placeholder="Your name"
          value={anonName}
          onBlur={event => {
            window.clearTimeout(onChangeDebounceTimer);

            // Do not call changeUsername when user clicks on Connect with Loom
            // button, else the button will not receive onClick event
            // since we'd exit out of anonymous reaction mode.
            if (
              !connectWithLoomRef.current ||
              !connectWithLoomRef.current.contains(event.relatedTarget)
            ) {
              checkAndUpdateUsername(event.target.value);
            }
          }}
          onKeyDown={onKeyDown}
          onChange={onInputChange}
        />
        <div ref={connectWithLoomRef}>
          <SharedAuthButton
            source={SharedAuthSource.PUBLIC_REACTION}
            isCollapsed={Boolean(anonName)}
          />
        </div>
      </Container>
    </div>
  );
};

export default AnonymousNameInput;
